import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PageHeader from '../components/PageHeader';
import shipmentService from '../services/shipmentService';
import DeleteRecordModal from '../components/DeleteRecordModal';
import SearchBar from '../components/SearchBar';
import DateRangeFilter from '../components/DateRangeFilter';
import { useT } from '../i18n/LanguageContext';
import useVisibleRefresh from '../hooks/useVisibleRefresh';
import { captureSession, isSessionCurrent } from '../utils/authSession';
import ShippingDocuments from '../components/ShippingDocuments';
import ShippingReviewDialog from '../components/ShippingReviewDialog';
import ShippingDocumentReview from '../components/ShippingDocumentReview';
import TrackingIntakeReview from '../components/TrackingIntakeReview';
import { shippingId } from '../services/shippingDocumentService';
import ShipmentListTable from '../features/shipments/ShipmentListTable';

const orderStatus = (row) => {
  const status = row.orderStatus !== undefined ? row.orderStatus : row.order?.status;
  return ['approved', 'shipped', 'completed', 'cancelled', 'submitted'].includes(status)
    ? status
    : null;
};
const statusConfig = {
  completed: { label: 'Completed', bg: 'bg-[rgba(222,252,237,0.6)]', text: 'text-[#007a55]' },
};

/* ─── 3-dot Actions Dropdown with status change (portal-based) ─── */
function ActionsDropdown({ currentStatus, onStatusChange, disabled }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, dropUp: false });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (
        btnRef.current &&
        !btnRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const statusOptions = Object.entries(statusConfig).filter(([key]) => key !== currentStatus);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropUp = spaceBelow < 180;
      setPos({
        top: dropUp ? rect.top + window.scrollY : rect.bottom + window.scrollY + 4,
        left: rect.right - 170,
        dropUp,
      });
    }
    setOpen(!open);
  };

  return (
    <div>
      <button
        ref={btnRef}
        disabled={disabled}
        onClick={handleToggle}
        className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded"
      >
        <span className="material-symbols-outlined text-[#64748b] text-[20px]">more_vert</span>
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: pos.dropUp ? undefined : pos.top,
              bottom: pos.dropUp ? `${window.innerHeight - pos.top + 4}px` : undefined,
              left: pos.left,
              zIndex: 9999,
            }}
            className="bg-white border border-[#e2e8f0] rounded-lg shadow-lg w-[170px] py-1"
          >
            <p className="px-3 py-1.5 text-[10px] font-semibold text-[#64748b] uppercase tracking-wider">
              {t('Change Status')}
            </p>
            {statusOptions.map(([key, config]) => (
              <button
                key={key}
                onClick={() => {
                  onStatusChange(key);
                  setOpen(false);
                }}
                className="flex items-center gap-2.5 px-3 py-2 w-full hover:bg-gray-50 text-xs font-medium text-[#0f172a]"
              >
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${config.bg} ${config.text}`}
                >
                  {t(config.label)}
                </span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

/* ─── Copy Tracking ID button ─── */
export default function ShipmentTracking() {
  const t = useT();
  const [shipments, setShipments] = useState([]);
  // The shipment an admin has asked to delete, pending confirmation.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [documentTarget, setDocumentTarget] = useState(null);
  const [review, setReview] = useState('');
  const closeDocuments = useCallback(() => setDocumentTarget(null), []);
  const closeReview = useCallback(() => setReview(''), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [completing, setCompleting] = useState(false);
  const completingRef = useRef(false);
  const mounted = useRef(false);
  const seq = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // A shipment carries neither an order id nor a submitter of its own - the server resolves both
  // through the Order it points at (and, for the submitter, the User that Order points at), which
  // is what makes searching by either work here.
  const [search, setSearch] = useState('');
  const [dates, setDates] = useState({ dateFrom: '', dateTo: '' });

  const fetchShipments = useCallback(
    async ({ background = false } = {}) => {
      const mine = ++seq.current;
      const session = captureSession();
      if (!background) {
        setLoading(true);
        setError('');
      }
      try {
        const params = { page, limit: 20 };
        if (search) params.search = search;
        if (dates.dateFrom) params.dateFrom = dates.dateFrom;
        if (dates.dateTo) params.dateTo = dates.dateTo;
        const res = await shipmentService.getShipments(params);
        if (mine !== seq.current || !isSessionCurrent(session)) return;
        const body = res.data?.data;
        if (!Array.isArray(body?.data)) throw new Error('Shipment list unavailable');
        setShipments(body.data);
        setTotalPages(Math.max(1, Number(body.totalPages) || 1));
        setError('');
      } catch {
        if (mine !== seq.current || !isSessionCurrent(session)) return;
        if (!background) setShipments([]);
        setError('Could not refresh shipments. Statuses may be out of date.');
      } finally {
        if (mine === seq.current && isSessionCurrent(session)) setLoading(false);
      }
    },
    [search, dates, page]
  );
  const latestFetch = useRef(fetchShipments);
  useEffect(() => {
    latestFetch.current = fetchShipments;
  }, [fetchShipments]);

  useEffect(() => {
    fetchShipments();
    return () => {
      seq.current += 1;
    };
  }, [fetchShipments]);
  useVisibleRefresh(
    fetchShipments,
    !loading && !deleteTarget && !completing && !documentTarget && !review
  );
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleStatusChange = async (shipment, newStatus) => {
    if (
      completingRef.current ||
      error ||
      newStatus !== 'completed' ||
      orderStatus(shipment) !== 'shipped' ||
      !['pending', 'in_transit'].includes(shipment.status)
    )
      return;
    completingRef.current = true;
    seq.current += 1;
    setCompleting(true);
    const session = captureSession();
    try {
      await shipmentService.completeShipment(shipment);
      if (mounted.current && isSessionCurrent(session))
        await latestFetch.current({ background: true });
    } catch {
      if (mounted.current && isSessionCurrent(session)) {
        setError('Could not confirm completion. Refresh shipments before trying again.');
      }
    } finally {
      completingRef.current = false;
      if (mounted.current && isSessionCurrent(session)) setCompleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('Shipment Tracking')}
        subtitle={t('Shipment statuses match their orders and update automatically.')}
      >
        <SearchBar
          placeholder={t('Search shipments...')}
          className="w-[243px]"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <DateRangeFilter
          dateFrom={dates.dateFrom}
          dateTo={dates.dateTo}
          onChange={(value) => {
            setDates(value);
            setPage(1);
          }}
        />
      </PageHeader>

      <div className="mb-4 flex flex-wrap justify-end gap-3 text-sm">
        <button
          type="button"
          disabled={completing}
          onClick={() => setReview('tracking')}
          className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-[#0089ff]"
        >
          {t('Tracking reviews')}
        </button>
        <button
          type="button"
          disabled={completing}
          onClick={() => setReview('documents')}
          className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-[#0089ff]"
        >
          {t('Document reviews')}
        </button>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          {t(error)}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
        </div>
      ) : (
        <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto">
          <ShipmentListTable
            shipments={shipments}
            t={t}
            statusForShipment={(shipment) =>
              shipment.status === 'completed'
                ? 'completed'
                : orderStatus(shipment) || 'Status unavailable'
            }
            renderActions={(shipment) => {
              const currentOrderStatus = orderStatus(shipment);
              const orderId = shipment.order?.orderId || shipment.orderId || t('N/A');
              return (
                <>
                  <button
                    type="button"
                    disabled={completing || !shippingId(shipment._id)}
                    onClick={() => setDocumentTarget(shipment)}
                    aria-label={t('Shipping documents for order {id}', { id: orderId })}
                    title={t('Shipping documents')}
                    className="w-6 h-6 flex items-center justify-center rounded text-[#0089ff] hover:bg-blue-50 disabled:opacity-40"
                  >
                    <span className="material-symbols-outlined text-[18px]">description</span>
                  </button>
                  {currentOrderStatus === 'shipped' &&
                    ['pending', 'in_transit'].includes(shipment.status) && (
                      <ActionsDropdown
                        disabled={completing || !!error}
                        currentStatus={currentOrderStatus}
                        onStatusChange={(newStatus) => handleStatusChange(shipment, newStatus)}
                      />
                    )}
                  <button
                    type="button"
                    disabled={completing}
                    onClick={() => setDeleteTarget(shipment)}
                    aria-label={t('Delete shipment for order {id}', { id: orderId })}
                    title={t('Delete shipment')}
                    className="w-6 h-6 flex items-center justify-center rounded text-[#64748b] hover:bg-[#fef2f2] hover:text-[#b91c1c] transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </>
              );
            }}
          />
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-3 text-sm">
          <button disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>
            {t('Previous')}
          </button>
          <span>{t('Page {page} of {total}', { page, total: totalPages })}</span>
          <button
            disabled={page >= totalPages || loading}
            onClick={() => setPage((value) => value + 1)}
          >
            {t('Next')}
          </button>
        </div>
      )}
      <DeleteRecordModal
        open={!!deleteTarget}
        label={t('shipment for order {id}', {
          id: deleteTarget?.orderId || deleteTarget?.order?.orderId || '',
        })}
        onCancel={() => setDeleteTarget(null)}
        onDelete={() => shipmentService.deleteShipment(deleteTarget._id)}
        onDone={() => {
          setDeleteTarget(null);
          fetchShipments();
        }}
      />
      {documentTarget && (
        <ShippingReviewDialog title="Shipping documents" onClose={closeDocuments}>
          <p className="text-sm text-slate-500">
            {documentTarget.orderId || documentTarget.order?.orderId}
          </p>
          <ShippingDocuments shipmentId={documentTarget._id} />
        </ShippingReviewDialog>
      )}
      {review && (
        <ShippingReviewDialog
          title={review === 'tracking' ? 'Tracking reviews' : 'Document reviews'}
          onClose={closeReview}
        >
          {review === 'tracking' ? (
            <TrackingIntakeReview onApplied={() => latestFetch.current({ background: true })} />
          ) : (
            <ShippingDocumentReview />
          )}
        </ShippingReviewDialog>
      )}
    </div>
  );
}
